'use client';

import { useReducedMotion } from 'framer-motion';
import type { LottieRefCurrentProps } from 'lottie-react';
import { useNow, useTranslations } from 'next-intl';
import { type KeyboardEvent, type TransitionEvent, useEffect, useRef, useState } from 'react';
import { showsTawkBubble, TAWK_BUBBLE } from '@/lib/components/TawkToChat';
import { usePathname } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { LazyLottie } from '@/lib/ui-kit/lazy-lottie';
import { useAnimationData } from '@/lib/ui-kit/lottie-illustration';
import { cn } from '@/lib/ui-kit/utils';
import { nextUtcMidnight } from '@/lib/utils/dates';
import { formatCountdown } from '@/lib/utils/formatCountdown';
import { useDailyCardStore } from './dailyCardStore';
import { preloadDailyPick } from './dailyPickLoader';
import { useCoversMarked } from './useCoversMarked';
import { useDailyCardOffer } from './useDailyCardOffer';

/** Idle loop and the short hover burst, both from /public/animations. */
const LOTTIE_DEFAULT = 'daily-card-default';
const LOTTIE_HOVER = 'daily-card-hover';

/** Both layers share the composition's box, drawn past the 96×64 card area. */
const LAYER =
  'absolute top-1/2 left-1/2 h-[132px] w-[180px] -translate-x-1/2 -translate-y-1/2 pointer-events-none';

/** Figma: 16px from the right, 70px from the bottom. */
const INSET = { right: 16, bottom: 70 };
/** With the chat bubble in the corner: 24px to its left, bottoms aligned. */
const INSET_BESIDE_CHAT = {
  right: TAWK_BUBBLE.right + TAWK_BUBBLE.width + 24,
  bottom: TAWK_BUBBLE.bottom,
};

/** Closed by the cross, or making way for the order form: off past the right edge, fading. */
const AWAY = 'pointer-events-none translate-x-[calc(100%+16px)] opacity-0';

/** `setTimeout` overflows past ~24.8 days; a dismissal never lasts that long, this is a guard. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

interface TimerProps {
  target: Date | null;
}

function Timer({ target }: TimerProps) {
  const now = useNow({ updateInterval: 1000 });
  return <>{formatCountdown(target ?? nextUtcMidnight(now), now)}</>;
}

/**
 * The daily card floating over every page from 1024 up (below it the card sits
 * in the header). Hidden once today's card is taken, on the hub, and — via the
 * cross — until the next draw.
 */
export function DailyCardWidget() {
  // The hover layer stays up while its burst plays back out, not just while
  // hovered; the idle layer only hides once the hover layer covers it fully.
  const [hoverShown, setHoverShown] = useState(false);
  const [idleHidden, setIdleHidden] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Hidden until the client has compared the dismissal with the clock.
  const [dismissed, setDismissed] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const idleRef = useRef<LottieRefCurrentProps>(null);
  const hoverRef = useRef<LottieRefCurrentProps>(null);
  const t = useTranslations('rewardsHub.daily');
  const offer = useDailyCardOffer();
  const dismissedUntil = useDailyCardStore((s) => s.dismissedUntil);
  const dismiss = useDailyCardStore((s) => s.dismiss);
  const defaultData = useAnimationData(LOTTIE_DEFAULT);
  const hoverData = useAnimationData(LOTTIE_HOVER);
  const reduceMotion = useReducedMotion();
  const pathname = usePathname();
  // Needed by the hook below, so derived ahead of the usual order.
  const available = offer.available && offer.allowedHere;
  const besideChat = showsTawkBubble(pathname);
  const inset = besideChat ? INSET_BESIDE_CHAT : INSET;
  // Never over the bet slip: the card steps aside while it would cover it.
  const coversForm = useCoversMarked(rootRef, inset, `${pathname}:${available}`);

  // The cross hides the card until the next draw; it comes back on its own then.
  useEffect(() => {
    const left = dismissedUntil - Date.now();
    setDismissed(left > 0);
    if (left <= 0) return;
    const timer = window.setTimeout(() => setDismissed(false), Math.min(left, MAX_TIMEOUT_MS));
    return () => window.clearTimeout(timer);
  }, [dismissedUntil]);

  if (!available || dismissed) return null;

  const { day, nextAt, open } = offer;

  // The burst runs forward on enter and backward on leave, each from wherever
  // it is, so a quick in-and-out never jumps.
  const handlePointerEnter = () => {
    preloadDailyPick();
    setHoverShown(true);
    hoverRef.current?.setDirection(1);
    hoverRef.current?.play();
  };
  const handlePointerLeave = () => {
    hoverRef.current?.setDirection(-1);
    hoverRef.current?.play();
  };
  const handleHoverFadedIn = () => {
    if (hoverShown) setIdleHidden(true);
  };
  // Frame 0 of the burst is frame 0 of the idle loop, so restarting the loop
  // and swapping layers in one go is invisible — no fade, no blink.
  const handleHoverComplete = () => {
    if (hoverRef.current?.animationItem?.playDirection !== -1) return;
    idleRef.current?.goToAndPlay(0, true);
    setIdleHidden(false);
    setHoverShown(false);
  };
  const dismissUntilNextDraw = () => dismiss((nextAt ?? nextUtcMidnight(new Date())).getTime());
  const handleDismiss = () => (reduceMotion ? dismissUntilNextDraw() : setLeaving(true));
  // Only the widget's own slide counts, not the hover layers' fades inside it.
  const handleLeft = (event: TransitionEvent<HTMLDivElement>) => {
    if (leaving && event.target === event.currentTarget && event.propertyName === 'translate') {
      dismissUntilNextDraw();
    }
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') open();
  };

  return (
    <div
      ref={rootRef}
      onTransitionEnd={handleLeft}
      style={inset}
      className={cn(
        `fixed z-40 hidden w-[168px] flex-col items-center gap-4 px-1 transition-[translate,opacity] duration-300
        ease-[cubic-bezier(0.4,0,1,1)] min-[1024px]:flex`,
        (leaving || coversForm) && AWAY,
      )}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={t('widgetOpen')}
        onClick={open}
        onKeyDown={handleKeyDown}
        onFocus={preloadDailyPick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className="relative h-16 w-24 cursor-pointer"
      >
        {/* The composition is 360×264 with the cards in the middle: it is drawn
            larger than the 96×64 box and left to spill, glow and sparkles included. */}
        {defaultData ? (
          <div aria-hidden className={cn(LAYER, idleHidden && 'opacity-0')}>
            <LazyLottie lottieRef={idleRef} animationData={defaultData} loop autoplay />
          </div>
        ) : null}
        {hoverData ? (
          <div
            aria-hidden
            onTransitionEnd={handleHoverFadedIn}
            className={cn(LAYER, hoverShown ? 'transition-opacity duration-150' : 'opacity-0')}
          >
            <LazyLottie
              lottieRef={hoverRef}
              animationData={hoverData}
              loop={false}
              autoplay={false}
              onComplete={handleHoverComplete}
            />
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('widgetDismiss')}
        className="absolute top-0 right-1 flex size-4 cursor-pointer items-center justify-center text-v2-text-secondary transition-colors hover:text-v2-text-primary"
      >
        <Icon name="Close" size={16} />
      </button>
      {/* Same surface as the back-to-top button beside it; in dark it is the
          Figma chip exactly (#171a1b at 50%). */}
      <div className="flex items-center gap-1.5 rounded-full border border-v2-border bg-v2-surface-secondary/50 px-2.5 py-[5px] text-[12px] shadow-[0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-[12px]">
        <span className="font-semibold text-v2-text-primary">{t('day', { n: day })}</span>
        <span className="text-v2-text-secondary tabular-nums">
          <Timer target={nextAt} />
        </span>
      </div>
    </div>
  );
}
